/**
 * Sign-in, registration, password reset and invites: the form on a calm canvas,
 * beside an AgencyOS-style brand panel (hidden on small screens). The panel uses
 * the brand gradient, so a white-label agency's sign-in page is in its colors.
 */
import { CalendarDays, MessageSquare, SquareKanban, Users, Workflow } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTheme } from '../theme/ThemeProvider';

const FEATURES = [
  { icon: Users, title: 'Every client in one place', text: 'A sub-account per client, each with its own contacts and team.' },
  { icon: MessageSquare, title: 'One inbox', text: 'Texts, email and calls, with consent and quiet hours built in.' },
  { icon: CalendarDays, title: 'Bookings that fill themselves', text: 'Booking pages, reminders and round-robin calendars.' },
  { icon: SquareKanban, title: 'Pipelines and payments', text: 'Move deals forward and get paid from the same screen.' },
  { icon: Workflow, title: 'Automations', text: 'Follow-ups that run while you coach.' },
];

export function AuthLayout({ children }: { children: ReactNode }) {
  const { theme } = useTheme();
  return (
    <div className="grid min-h-screen lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <main className="page page-auth">{children}</main>
      <aside
        aria-label={`About ${theme.brandName}`}
        className="relative hidden overflow-hidden bg-gradient-to-br from-primary via-primary to-brand p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-center"
      >
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/10" />
        <div className="relative mx-auto w-full max-w-md">
          <p className="m-0 text-sm font-semibold uppercase tracking-wider text-primary-foreground/80">For agencies and coaches</p>
          <h2 className="m-0 mt-3 font-display text-4xl font-semibold leading-tight text-primary-foreground">Run every client from one place.</h2>
          <ul className="m-0 mt-8 grid list-none gap-3 p-0">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex items-start gap-3 rounded-[20px] border border-white/20 bg-white/10 p-4 backdrop-blur-md">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-white/20" aria-hidden="true">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="grid gap-0.5">
                  <span className="font-semibold">{title}</span>
                  <span className="text-sm text-primary-foreground/85">{text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}
